import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { customNotification } from "DesignSystem/Notifications/Notification";
import { WebService } from "services/web/webService";
import * as actions from "store/AiFeatures/GoalsCreateWithNovaAI/action";
import {
  getNovaConversationId,
  selectChatMessages,
} from "utils/getNovaConversationDetails";
import useNovaApi from "components/CreateWithNovaAiModal/hooks/useNovaApi.js";
import useNovaConversationTracker from "customHooks/useNovaConversationTracker";

const useNovaAiResponseApi = () => {
  const {
    renameConversation,
    fetchAllConversations,
    sendNovaChatFeedback,
    getUpdatedConversation,
  } = useNovaApi();
  const dispatch = useDispatch();
  const { setCurrentAccount } = useNovaConversationTracker();
  const accountId = useSelector((state) => state?.user?.accountId);
  const employeeId = useSelector((state) => state?.user?.employeeId);
  const conversationId = useSelector(getNovaConversationId);
  const userNovaChatMessages = useSelector((state) =>
    selectChatMessages(state, conversationId)
  );
  const isConversationChanged = useSelector(
    (state) => state?.aiFeatures?.goalsCreateWithNovaAI?.isConversationChanged
  );
  const updatedConversationId = useSelector(
    (state) => state?.aiFeatures?.goalsCreateWithNovaAI?.updatedConversationId
  );
  const updatedConversationTitle = useSelector(
    (state) =>
      state?.aiFeatures?.goalsCreateWithNovaAI?.updatedConversationTitle
  );
  const conversationLoadingStates =
    useSelector(
      (state) =>
        state?.aiFeatures?.goalsCreateWithNovaAI?.conversationLoadingStates
    ) || {};

  const {
    loading: isAiLoading = false,
    responseLoading: isAiResponseLoading = false,
  } = conversationLoadingStates[conversationId] || {};

  const showDefaultPrompts =
    conversationId && !isAiLoading && userNovaChatMessages?.length === 0;

  const [showUserMessage, setShowUserMessage] = useState(false);
  const [disableEditor, setDisableEditor] = useState(false);
  const [newConversationId, setNewConversationId] = useState(conversationId);
  const [chatPage, setChatPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  useEffect(() => {
    if (isConversationChanged && accountId) {
      const data = JSON.parse(
        localStorage.getItem("nova_conversation_data") || "{}"
      );
      data[employeeId] = {
        id: updatedConversationId,
        title: updatedConversationTitle,
      };
      localStorage.setItem("nova_conversation_data", JSON.stringify(data));
    }
  }, [
    isConversationChanged,
    accountId,
    employeeId,
    updatedConversationId,
    updatedConversationTitle,
  ]);

  const fetchDefaultPrompts = async () => {
    try {
      dispatch(actions.setConversationLoading(conversationId, true));
      const response = await WebService.get(
        `nova_conversations/default_prompts?type=goal_creation`
      );
      const prompts = response?.data?.success?.prompts;
      dispatch(actions.getNovaDefaultPromptsSuccess(prompts));
    } catch (error) {
      console.error("Failed to fetch default prompts:", error);
    } finally {
      dispatch(actions.setConversationLoading(conversationId, false));
    }
  };

  const createNewConversation = async () => {
    try {
      dispatch(actions.setConversationLoading(conversationId, true));
      dispatch(actions.setRegenerateAiResponse(conversationId, false));
      dispatch(actions.createNovaConversationRequest());
      const response = await WebService.post(
        `accounts/${accountId}/nova_conversations`,
        {
          nova_conversation: {
            ai_model_identifier: "gpt-4o-mini",
          },
        }
      );
      const conversation = response?.data?.success?.nova_conversation;
      if (conversation?.id) {
        dispatch(actions.createNovaConversationSuccess(conversation));
        fetchConversationMessages({ conversationId: conversation?.id });
        customNotification("success", "New Conversation Created");
        dispatch(actions.setRegenerateAiResponse(conversation?.id, false));
        setNewConversationId(conversation?.id);
        dispatch(
          actions.setChangedConversation(
            true,
            conversation?.id,
            conversation?.name
          )
        );
        const data = JSON.parse(
          localStorage.getItem("nova_conversation_data") || "{}"
        );
        data[employeeId] = { id: conversation?.id, title: conversation?.name };
        localStorage.setItem("nova_conversation_data", JSON.stringify(data));

        setCurrentAccount(employeeId);
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      console.error("Failed to create conversation", error);
      customNotification("error", "Failed to create new conversation");
    } finally {
      dispatch(actions.setConversationLoading(conversationId, false));
    }
  };

  const fetchConversationMessages = async ({
    conversationId: customConversationId,
    page = 1,
    limit = 10,
    append = false,
    direction = "append",
  } = {}) => {
    const convId = customConversationId || conversationId;
    if (!accountId || !convId) return;
    try {
      dispatch(actions.setConversationLoading(conversationId, true));
      setDisableEditor(false);
      dispatch(actions.fetchNovaMessagesRequest());
      const response = await WebService.get(
        `accounts/${accountId}/nova_conversations/${convId}/nova_chat_messages?page=${page}&limit=${limit}`
      );
      const novaChatMessages = response?.data?.success?.nova_messages;
      if (novaChatMessages) {
        const hasMoreChat = novaChatMessages.length >= limit;
        setHasMoreMessages(hasMoreChat);
        dispatch(
          actions.fetchNovaMessagesSuccess(
            novaChatMessages,
            convId,
            append,
            hasMoreChat,
            direction
          )
        );
      }
      setChatPage(page);
      if (novaChatMessages.length === 0 && !append) {
        fetchDefaultPrompts();
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        setDisableEditor(true);
        customNotification(
          "error",
          "Conversation Not Found",
          "Please create a new conversation."
        );
        const data = JSON.parse(
          localStorage.getItem("nova_conversation_data") || "{}"
        );
        delete data[employeeId];
        localStorage.setItem("nova_conversation_data", JSON.stringify(data));
      }
      dispatch(
        actions.fetchNovaMessagesFailure(
          error.message || "Failed to fetch messages"
        )
      );
      console.error("Error fetching messages:", error);
    } finally {
      dispatch(actions.setConversationLoading(conversationId, false));
    }
  };

  const sendChatMessage =
    ({ conversationId, userMessage }) =>
    async (dispatch, getState) => {
      try {
        setShowUserMessage(true);
        dispatch(actions.setConversationResponseLoading(conversationId, true));
        dispatch(actions.setConversationLoading(conversationId, true));
        dispatch(actions.setDisableHeaderOptions(true));
        dispatch(actions.sendNovaChatMessageRequest());
        dispatch(actions.setRegenerateAiResponse(conversationId, false));
        const userMsg = {
          text: userMessage,
          sender: "user",
        };
        dispatch(actions.sendNovaChatMessageSuccess(conversationId, userMsg));
        const response = await WebService.post(
          `accounts/${accountId}/nova_conversations/${conversationId}/chat`,
          {
            nova_conversation: {
              ai_model_identifier: "gpt-4o-mini",
              user_message: userMessage,
            },
          }
        );
        const {
          ai_response,
          user_nova_chat_message_id,
          assistant_nova_chat_message_id,
          conversation_name,
        } = response.data.success;

        const activeConversationId = getNovaConversationId(getState());
        if (conversationId === activeConversationId && conversation_name) {
          dispatch(
            actions.updateConversationTitleSuccess(
              conversationId,
              conversation_name
            )
          );
          const state = getState();
          const employeeId = state.user.employeeId;
          const data = JSON.parse(
            localStorage.getItem("nova_conversation_data") || "{}"
          );
          if (data[employeeId]) {
            data[employeeId].title = conversation_name;
            localStorage.setItem(
              "nova_conversation_data",
              JSON.stringify(data)
            );
          }
        }

        if (ai_response) {
          const botMsg = {
            text: ai_response,
            sender: "bot",
          };
          dispatch(actions.sendNovaChatMessageSuccess(conversationId, botMsg));
          dispatch(actions.setUserMessageId(user_nova_chat_message_id));
          dispatch(actions.setAIMessageId(assistant_nova_chat_message_id));
          setTimeout(() => {
            fetchConversationMessages({ conversationId: conversationId });
          }, 10);
        } else {
          throw new Error("Invalid AI response");
        }
      } catch (error) {
        console.error("Error sending message:", error);
        dispatch(actions.setRegenerateAiResponse(conversationId, true));
        const userMsgIdFromError =
          error?.response?.data?.error?.user_nova_chat_message_id;
        if (userMsgIdFromError) {
          dispatch(actions.setUserMessageId(userMsgIdFromError));
        }
        dispatch(
          actions.sendNovaChatMessageFailure(error.message || "Unknown error")
        );
        dispatch(
          actions.sendNovaChatMessageSuccess(conversationId, {
            text: "Something went wrong. Please try again.",
            sender: "bot",
            hasError: true,
          })
        );
        setTimeout(() => {
          fetchConversationMessages({ conversationId: conversationId });
        }, 10);
      } finally {
        dispatch(actions.setConversationResponseLoading(conversationId, false));
        dispatch(actions.setConversationLoading(conversationId, false));
        dispatch(actions.setDisableHeaderOptions(false));
      }
    };

  const regenerateChatMessage = async ({
    conversationId,
    userMessageId,
    setFromRegenerate,
  }) => {
    try {
      dispatch(actions.setConversationResponseLoading(conversationId, true));
      dispatch(actions.setConversationLoading(conversationId, true));
      dispatch(actions.setDisableHeaderOptions(true));
      setFromRegenerate(true);
      dispatch(actions.regenerateNovaChatMessageRequest());
      dispatch(actions.setRegenerateAiResponse(conversationId, false));
      const response = await WebService.post(
        `accounts/${accountId}/nova_conversations/${conversationId}/regenerate_response`,
        {
          nova_conversation: {
            ai_model_identifier: "gpt-4o-mini",
            message_id: userMessageId,
          },
        }
      );
      const aiResponse = response?.data?.success?.ai_response;
      if (aiResponse) {
        const botMsg = {
          text: aiResponse,
          sender: "bot",
        };
        dispatch(
          actions.regenerateNovaChatMessageSuccess(conversationId, botMsg)
        );
        setTimeout(() => {
          fetchConversationMessages({ conversationId });
        }, 10);
      } else {
        throw new Error("No AI response");
      }
    } catch (error) {
      console.error("Error regenerating message:", error);
      dispatch(actions.setRegenerateAiResponse(conversationId, true));
      dispatch(
        actions.regenerateNovaChatMessageFailure(
          error.message || "Unknown error"
        )
      );
      dispatch(
        actions.sendNovaChatMessageSuccess(conversationId, {
          text: "Something went wrong while regenerating. Please try again.",
          sender: "bot",
          hasError: true,
        })
      );
    } finally {
      dispatch(actions.setConversationResponseLoading(conversationId, false));
      dispatch(actions.setConversationLoading(conversationId, false));
      dispatch(actions.setDisableHeaderOptions(false));
      setFromRegenerate(false);
    }
  };

  return {
    propsForGoalsNovaAi: {
      createNewConversation,
      isAiLoading,
      isAiResponseLoading,
      showDefaultPrompts,
      fetchDefaultPrompts,
      conversationId,
      fetchConversationMessages,
      fetchAllConversations,
      sendChatMessage,
      renameConversation,
      showUserMessage,
      setShowUserMessage,
      newConversationId,
      setNewConversationId,
      regenerateChatMessage,
      sendNovaChatFeedback,
      getUpdatedConversation,
      chatPage,
      setChatPage,
      hasMoreMessages,
      setHasMoreMessages,
      disableEditor,
    },
  };
};

export default useNovaAiResponseApi;
